// @ts-nocheck
/**
 * Communication Center Component for Teachers
 * Manage class communications, announcements, and parent notifications
 */

import React, { useState, useEffect } from 'react';
import {
  Tile,
  Grid,
  Column,
  Button,
  Stack,
  DataTable,
  Tag,
  Modal,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Checkbox,
  InlineNotification,
  Loading,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';

const {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectAll,
  TableSelectRow,
} = DataTable;

import {
  SendAlt,
  Email,
  Notification,
  Chat,
  Calendar,
  UserMultiple,
  Document,
  Time,
  CheckmarkFilled,
  View,
  Template,
} from '@carbon/icons-react';

import { api } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';

interface Communication {
  communication_id: string;
  subject: string;
  message: string;
  type: 'announcement' | 'assignment_notification' | 'grade_notification' | 'reminder' | 'general';
  recipients: string[];
  include_parents: boolean;
  status: 'sent' | 'scheduled' | 'draft';
  sent_at?: string;
  scheduled_for?: string;
  read_count?: number;
  total_recipients?: number;
}

interface Student {
  student_id: string;
  name: string;
  email: string;
  parent_email?: string;
}

interface Template {
  template_id: string;
  name: string;
  subject: string;
  message: string;
  type: string;
}

const CommunicationCenter: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [communicationForm, setCommunicationForm] = useState({
    subject: '',
    message: '',
    type: 'announcement',
    includeParents: false,
    sendImmediately: true,
    scheduledDate: '',
    scheduledTime: '',
  });

  useEffect(() => {
    fetchCommunications();
    fetchStudents();
    loadTemplates();
  }, []);

  const fetchCommunications = async () => {
    try {
      setLoading(true);
      // API call would go here
      // const response = await api.get('/v1/teacher/communications');

      // Mock data for now
      setCommunications([
        {
          communication_id: '1',
          subject: 'Upcoming Math Quiz - Chapter 5',
          message: 'Dear students, we will have a quiz on Chapter 5 next Monday.',
          type: 'assignment_notification',
          recipients: ['student1', 'student2', 'student3'],
          include_parents: true,
          status: 'sent',
          sent_at: new Date(Date.now() - 86400000).toISOString(),
          read_count: 18,
          total_recipients: 25,
        },
        {
          communication_id: '2',
          subject: 'Field Trip Permission Forms',
          message: 'Please submit your signed permission forms by Friday.',
          type: 'reminder',
          recipients: ['all'],
          include_parents: true,
          status: 'sent',
          sent_at: new Date(Date.now() - 2 * 86400000).toISOString(),
          read_count: 22,
          total_recipients: 25,
        },
        {
          communication_id: '3',
          subject: 'Parent-Teacher Conference Schedule',
          message: 'Sign-ups for parent-teacher conferences are now open.',
          type: 'announcement',
          recipients: ['all'],
          include_parents: true,
          status: 'scheduled',
          scheduled_for: new Date(Date.now() + 86400000).toISOString(),
          total_recipients: 25,
        },
      ]);
    } catch (error) {
      console.error('Failed to fetch communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      // API call would go here
      // const response = await api.get('/v1/teacher/students');

      // Mock data for now
      setStudents([
        {
          student_id: '1',
          name: 'Alice Johnson',
          email: 'alice@student.sigma08.com',
          parent_email: 'parent.alice@sigma08.com',
        },
        {
          student_id: '2',
          name: 'Bob Smith',
          email: 'bob@student.sigma08.com',
          parent_email: 'parent.bob@sigma08.com',
        },
        {
          student_id: '3',
          name: 'Carol Davis',
          email: 'carol@student.sigma08.com',
          parent_email: 'parent.carol@sigma08.com',
        },
      ]);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const loadTemplates = () => {
    // Load predefined templates
    setTemplates([
      {
        template_id: '1',
        name: 'Assignment Reminder',
        subject: 'Assignment Due Soon - [SUBJECT]',
        message:
          'Dear students,\n\nThis is a reminder that your [ASSIGNMENT_NAME] is due on [DUE_DATE].\n\nPlease make sure to submit it on time.',
        type: 'reminder',
      },
      {
        template_id: '2',
        name: 'Grade Notification',
        subject: 'Grades Posted - [ASSIGNMENT_NAME]',
        message:
          'Dear students and parents,\n\nGrades for [ASSIGNMENT_NAME] have been posted.\n\nYou can view them in the student portal.',
        type: 'grade_notification',
      },
      {
        template_id: '3',
        name: 'Class Announcement',
        subject: 'Class Update',
        message: 'Dear class,\n\n[YOUR_MESSAGE_HERE]\n\nBest regards,\n[TEACHER_NAME]',
        type: 'announcement',
      },
    ]);
  };

  const handleSendCommunication = async () => {
    try {
      const payload = {
        recipients: selectedStudents.length > 0 ? selectedStudents : ['all'],
        subject: communicationForm.subject,
        message: communicationForm.message,
        type: communicationForm.type,
        includeParents: communicationForm.includeParents,
        classId: 'default_class',
      };

      const response = await api.post('/v1/teacher/communications/send', payload);

      if (response.data?.success) {
        setIsComposeModalOpen(false);
        setCommunicationForm({
          subject: '',
          message: '',
          type: 'announcement',
          includeParents: false,
          sendImmediately: true,
          scheduledDate: '',
          scheduledTime: '',
        });
        setSelectedStudents([]);
        fetchCommunications();
      }
    } catch (error) {
      console.error('Failed to send communication:', error);
    }
  };

  const applyTemplate = (template: Template) => {
    setCommunicationForm({
      ...communicationForm,
      subject: template.subject,
      message: template.message,
      type: template.type as any,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'green';
      case 'scheduled':
        return 'blue';
      case 'draft':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'announcement':
        return <Notification size={20} />;
      case 'assignment_notification':
        return <Document size={20} />;
      case 'grade_notification':
        return <CheckmarkFilled size={20} />;
      case 'reminder':
        return <Time size={20} />;
      default:
        return <Email size={20} />;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Loading withOverlay={false} />
        <div style={{ marginTop: 16 }}>Loading communication center...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 400, marginBottom: 8 }}>Communication Center</h1>
        <p style={{ color: '#525252' }}>Send announcements, reminders, and notifications to students and parents</p>
      </div>

      {/* Statistics */}
      <Grid style={{ marginBottom: 24 }}>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <SendAlt size={24} style={{ color: '#0f62fe' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Messages Sent</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {communications.filter(c => c.status === 'sent').length}
            </div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <UserMultiple size={24} style={{ color: '#198038' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Total Recipients</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {students.length * 2} {/* Students + Parents */}
            </div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <Calendar size={24} style={{ color: '#8a3ffc' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Scheduled</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>
              {communications.filter(c => c.status === 'scheduled').length}
            </div>
          </Tile>
        </Column>
        <Column sm={4} md={2} lg={3}>
          <Tile>
            <div style={{ marginBottom: 8 }}>
              <CheckmarkFilled size={24} style={{ color: '#24a148' }} />
            </div>
            <div style={{ fontSize: '0.875rem', color: '#525252' }}>Read Rate</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>72%</div>
          </Tile>
        </Column>
      </Grid>

      {/* Action Buttons */}
      <Stack gap={5} style={{ marginBottom: 24 }}>
        <Button kind="primary" renderIcon={SendAlt} onClick={() => setIsComposeModalOpen(true)}>
          Compose Message
        </Button>
        <Button kind="secondary" renderIcon={Template}>
          Message Templates
        </Button>
        <Button kind="tertiary" renderIcon={View}>
          View Analytics
        </Button>
      </Stack>

      {/* Communications Tabs */}
      <Tabs>
        <TabList>
          <Tab>All Messages</Tab>
          <Tab>Sent</Tab>
          <Tab>Scheduled</Tab>
          <Tab>Drafts</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {/* All Communications Table */}
            <DataTable
              rows={communications.map(c => ({ ...c, id: c.communication_id }))}
              headers={[
                { key: 'subject', header: 'Subject' },
                { key: 'type', header: 'Type' },
                { key: 'recipients', header: 'Recipients' },
                { key: 'status', header: 'Status' },
                { key: 'sent_at', header: 'Date' },
                { key: 'read_count', header: 'Read' },
              ]}
              render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => {
                        const comm = communications[i];
                        return (
                          <TableRow {...getRowProps({ row })} key={row.id}>
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {getTypeIcon(comm.type)}
                                <div>
                                  <div style={{ fontWeight: 500 }}>{comm.subject}</div>
                                  <div style={{ fontSize: '0.875rem', color: '#525252' }}>
                                    {comm.message.substring(0, 50)}...
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{comm.type.replace('_', ' ')}</TableCell>
                            <TableCell>
                              <Stack gap={2}>
                                <span>{comm.total_recipients || comm.recipients.length} students</span>
                                {comm.include_parents && (
                                  <Tag type="blue" size="sm">
                                    + Parents
                                  </Tag>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Tag type={getStatusColor(comm.status)}>{comm.status}</Tag>
                            </TableCell>
                            <TableCell>
                              {comm.sent_at
                                ? new Date(comm.sent_at).toLocaleDateString()
                                : comm.scheduled_for
                                  ? `Scheduled: ${new Date(comm.scheduled_for).toLocaleDateString()}`
                                  : 'Draft'}
                            </TableCell>
                            <TableCell>
                              {comm.read_count !== undefined ? `${comm.read_count}/${comm.total_recipients}` : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification kind="info" title="Sent Messages" subtitle="View all sent communications here" />
          </TabPanel>
          <TabPanel>
            <InlineNotification
              kind="info"
              title="Scheduled Messages"
              subtitle="View and manage scheduled communications"
            />
          </TabPanel>
          <TabPanel>
            <InlineNotification kind="info" title="Draft Messages" subtitle="View and edit draft messages" />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Compose Message Modal */}
      <Modal
        open={isComposeModalOpen}
        modalHeading="Compose Message"
        primaryButtonText="Send"
        secondaryButtonText="Cancel"
        size="lg"
        onRequestSubmit={handleSendCommunication}
        onRequestClose={() => setIsComposeModalOpen(false)}
      >
        <Form>
          {/* Template Selection */}
          <FormGroup>
            <Select
              id="template-select"
              labelText="Use Template (Optional)"
              onChange={e => {
                const template = templates.find(t => t.template_id === e.target.value);
                if (template) applyTemplate(template);
              }}
            >
              <SelectItem value="" text="Select a template..." />
              {templates.map(template => (
                <SelectItem key={template.template_id} value={template.template_id} text={template.name} />
              ))}
            </Select>
          </FormGroup>

          {/* Message Type */}
          <FormGroup>
            <Select
              id="message-type"
              labelText="Message Type"
              value={communicationForm.type}
              onChange={e => setCommunicationForm({ ...communicationForm, type: e.target.value })}
            >
              <SelectItem value="announcement" text="Announcement" />
              <SelectItem value="assignment_notification" text="Assignment Notification" />
              <SelectItem value="grade_notification" text="Grade Notification" />
              <SelectItem value="reminder" text="Reminder" />
              <SelectItem value="general" text="General" />
            </Select>
          </FormGroup>

          {/* Subject */}
          <FormGroup>
            <TextInput
              id="message-subject"
              labelText="Subject"
              placeholder="Enter message subject"
              value={communicationForm.subject}
              onChange={e => setCommunicationForm({ ...communicationForm, subject: e.target.value })}
              required
            />
          </FormGroup>

          {/* Message */}
          <FormGroup>
            <TextArea
              id="message-body"
              labelText="Message"
              placeholder="Enter your message..."
              rows={6}
              value={communicationForm.message}
              onChange={e => setCommunicationForm({ ...communicationForm, message: e.target.value })}
              required
            />
          </FormGroup>

          {/* Recipients */}
          <FormGroup legendText="Recipients">
            <Checkbox
              id="select-all-students"
              labelText="All Students"
              checked={selectedStudents.length === 0}
              onChange={e => {
                if (e.target.checked) {
                  setSelectedStudents([]);
                }
              }}
            />
            {students.map(student => (
              <Checkbox
                key={student.student_id}
                id={`student-${student.student_id}`}
                labelText={student.name}
                checked={selectedStudents.includes(student.student_id)}
                onChange={e => {
                  if (e.target.checked) {
                    setSelectedStudents([...selectedStudents, student.student_id]);
                  } else {
                    setSelectedStudents(selectedStudents.filter(id => id !== student.student_id));
                  }
                }}
              />
            ))}
          </FormGroup>

          {/* Include Parents */}
          <FormGroup>
            <Checkbox
              id="include-parents"
              labelText="Also send to parents"
              checked={communicationForm.includeParents}
              onChange={e => setCommunicationForm({ ...communicationForm, includeParents: e.target.checked })}
            />
          </FormGroup>

          {/* Send Options */}
          <FormGroup legendText="Send Options">
            <RadioButtonGroup
              name="send-options"
              defaultSelected="immediately"
              onChange={value =>
                setCommunicationForm({ ...communicationForm, sendImmediately: value === 'immediately' })
              }
            >
              <RadioButton labelText="Send immediately" value="immediately" />
              <RadioButton labelText="Schedule for later" value="schedule" />
            </RadioButtonGroup>
          </FormGroup>
        </Form>
      </Modal>
    </div>
  );
};

export default CommunicationCenter;
